import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FMCheckboxInput from '../../../common/fm-checkbox-input/fm-checkbox-input';
import { FMButton } from '@own/fds-react';
import './img-spc-vehicle-service-ph.scss';
import {
    ImgSpcFieldLabelsContentFragment,
    ImgSpcHeadingContentFragment,
    ImgSpcIolmContentFragment,
    ImgSpcPomContentFragment,
} from '../../../../models/img-service-price-calculator/img-service-price-calculator';
import {
    ImgSpcDealerModelDetailsApiDataTHPHVN,
    ImgSpcResultsForDerivativeApiDataData,
} from '../../../../models/serice-price-calculator-model';
import { useAnalytics } from '../../../../hooks/use-analytics';
import DOMPurify from 'dompurify';
import ServiceIntervalDropdown from './img-spc-serviceinterval-dropdown';

export interface ChooseYourServiceProps {
    service: any;
    intervalDropdown: any;
    serviceInterval: any;
    headersLabel?: ImgSpcHeadingContentFragment;
    fieldsLabel?: ImgSpcFieldLabelsContentFragment;
    pomLabel?: ImgSpcPomContentFragment;
    iolmLabel?: ImgSpcIolmContentFragment;
    imgSpcDealerModelData: ImgSpcDealerModelDetailsApiDataTHPHVN;
    imgSpcDerivativeData: ImgSpcResultsForDerivativeApiDataData | null;
    derivativeName: string;
    pomChecked: boolean;
    onPomCheckedChange: (checked: boolean) => void;
    iolmChecked: boolean;
    onIolmCheckedChange: (checked: boolean) => void;
    freeLaborCouponChecked: boolean;
    onFreeLaborCouponCheckedChange: (checked: boolean) => void;
    onInspectionDiscountApplicableChange: (applicable: boolean) => void;
}

const ChooseYourServicePH = ({
    service,
    intervalDropdown,
    serviceInterval,
    headersLabel,
    fieldsLabel,
    pomLabel,
    iolmLabel,
    imgSpcDealerModelData,
    imgSpcDerivativeData,
    derivativeName,
    pomChecked,
    onPomCheckedChange,
    iolmChecked,
    onIolmCheckedChange,
    freeLaborCouponChecked,
    onFreeLaborCouponCheckedChange,
    onInspectionDiscountApplicableChange,
}: ChooseYourServiceProps) => {
    const [
        isVehicleInspectionChecked,
        setIsVehicleInspectionChecked,
    ] = useState(false);
    const [showPeaceOfMindCheckbox, setShowPeaceOfMindCheckbox] = useState(
        false
    );
    const [isIolmAvailable, setIsIolmAvailable] = useState(false);
    const [isLaborFree, setIsLaborFree] = useState(false);
    const [fireEvents] = useAnalytics();

    useEffect(() => {
        const iolmJson = imgSpcDerivativeData?.results?.[0]?.iolmJson;
        const iolmAvailable =
            iolmJson !== null &&
            typeof iolmJson === 'object' &&
            !Array.isArray(iolmJson) &&
            Object.keys(iolmJson).length > 0;
        setIsIolmAvailable(iolmAvailable);
    }, [imgSpcDerivativeData]);

    useEffect(() => {
        const valueToParse =
            typeof serviceInterval === 'object' && serviceInterval !== null
                ? serviceInterval.value
                : serviceInterval;

        const phFreeLaborIntervals = isVehicleInspectionChecked
            ? ['1 Year / 10000 Kms', '2 Years / 20000 Kms']
            : ['1 Year / 10000 Kms'];

        const laborFree = valueToParse
            ? phFreeLaborIntervals.includes(valueToParse)
            : false;

        setIsLaborFree(laborFree);

        const applicableDiscountIntervals = [
            '1 Year / 10000 Kms',
            '2 Years / 20000 Kms',
        ];
        const inspectionDiscountApplicable =
            isVehicleInspectionChecked &&
            (valueToParse
                ? applicableDiscountIntervals.includes(valueToParse)
                : false);
        onInspectionDiscountApplicableChange(inspectionDiscountApplicable);
    }, [
        serviceInterval,
        isVehicleInspectionChecked,
        onInspectionDiscountApplicableChange,
    ]);

    useEffect(() => {
        if (!iolmChecked && freeLaborCouponChecked) {
            onFreeLaborCouponCheckedChange(false);
        }
    }, [iolmChecked, onFreeLaborCouponCheckedChange, freeLaborCouponChecked]);

    const handleVehicleInspectionChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;
            setIsVehicleInspectionChecked(checked);
            setShowPeaceOfMindCheckbox(checked);
            if (!checked && pomChecked) {
                onPomCheckedChange(false);
            }
        },
        [pomChecked, onPomCheckedChange]
    );

    const handlePeaceOfMindChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onPomCheckedChange(e.target.checked);
        },
        [onPomCheckedChange]
    );

    const handleOilChangeIOLMChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;
            if (!checked && freeLaborCouponChecked) {
                onFreeLaborCouponCheckedChange(false);
            }
            onIolmCheckedChange(checked);
        },
        [
            onIolmCheckedChange,
            freeLaborCouponChecked,
            onFreeLaborCouponCheckedChange,
        ]
    );

    const handleFreeLaborCouponChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onFreeLaborCouponCheckedChange(e.target.checked);
        },
        [onFreeLaborCouponCheckedChange]
    );

    const handleIntervalDropdown = useCallback(
        (event: any) => {
            intervalDropdown(event);
        },
        [intervalDropdown]
    );

    const calculateButtonEnable = useMemo(() => {
        const isServiceIntervalSelected =
            !!serviceInterval &&
            (typeof serviceInterval !== 'string' ||
                serviceInterval.trim() !== '');

        return !!(
            pomChecked ||
            isServiceIntervalSelected ||
            (isIolmAvailable && iolmChecked)
        );
    }, [pomChecked, serviceInterval, isIolmAvailable, iolmChecked]);

    const renderServiceIntervalSection = !pomChecked;

    const renderPomDescription = pomChecked && pomLabel?.descriptionPOM?.html;

    function isVariantRetail(
        derivativeName: string,
        imgSpcDealerModelData: ImgSpcDealerModelDetailsApiDataTHPHVN
    ) {
        if (!imgSpcDealerModelData?.modelsInfo) {
            return false;
        }
        for (const nameplate of imgSpcDealerModelData.modelsInfo) {
            if (!nameplate.models) continue;
            for (const model of nameplate.models) {
                if (model.variantName === derivativeName) {
                    return model.retailFlag === 'Y';
                }
            }
        }
        return false;
    }

    return (
        <section className="img-spc-service-interval-ph">
            <h3 className="service-interval-title">
                {headersLabel?.serviceIntervalTitle}
            </h3>

            {isVariantRetail(derivativeName, imgSpcDealerModelData) && (
                <div className="vehicle-inspection">
                    <FMCheckboxInput
                        label={pomLabel?.retailCheckBoxLabelForPH}
                        value="vehicleInspection"
                        wrapperClassName="checkbox-onlyiolm"
                        checked={isVehicleInspectionChecked}
                        onChange={handleVehicleInspectionChange}
                        disabled={
                            !isVariantRetail(
                                derivativeName,
                                imgSpcDealerModelData
                            )
                        }
                    />

                    {showPeaceOfMindCheckbox && (
                        <FMCheckboxInput
                            label={pomLabel?.pomService}
                            value="peaceOfMind"
                            wrapperClassName="peace-of-mind-inspection"
                            checked={pomChecked}
                            onChange={handlePeaceOfMindChange}
                            disabled={
                                !isVariantRetail(
                                    derivativeName,
                                    imgSpcDealerModelData
                                )
                            }
                        />
                    )}
                </div>
            )}

            {renderPomDescription && (
                <div
                    className="vehicle-service-description"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                            pomLabel.descriptionPOM.html
                        ),
                    }}
                />
            )}

            {renderServiceIntervalSection && (
                <>
                    <ServiceIntervalDropdown
                        service={service}
                        serviceInterval={serviceInterval}
                        handleIntervalDropdown={handleIntervalDropdown}
                        fieldsLabel={fieldsLabel}
                        disabled={service.length === 0}
                    />

                    {isIolmAvailable && (
                        <div className="vehicle-service-iolm">
                            <FMCheckboxInput
                                label={iolmLabel?.oilChangeDue}
                                value="oilChangeIOLM"
                                name="oilChangeIOLM"
                                wrapperClassName="checkbox-oilchangeiolm-wrapper"
                                checked={iolmChecked}
                                onChange={handleOilChangeIOLMChange}
                                disabled={!isIolmAvailable}
                            />

                            <FMCheckboxInput
                                label={iolmLabel?.freeLaborCoupon}
                                wrapperClassName="checkbox-free-coupon-labor"
                                value="freeLaborCoupon"
                                checked={freeLaborCouponChecked}
                                onChange={handleFreeLaborCouponChange}
                                disabled={
                                    !isIolmAvailable ||
                                    !iolmChecked ||
                                    (!!serviceInterval && !isLaborFree)
                                }
                            />
                        </div>
                    )}

                    {isIolmAvailable && (
                        <>
                            {iolmLabel?.descriptionToBeDisplayedForIOLM
                                ?.html && (
                                <div
                                    className="vehicle-service-oil-change"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            iolmLabel
                                                .descriptionToBeDisplayedForIOLM
                                                .html
                                        ),
                                    }}
                                />
                            )}
                            {iolmLabel?.disclosureDataForFreeLabourCost
                                ?.html && (
                                <div
                                    className="vehicle-service-free-labour-disclosure"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            iolmLabel
                                                .disclosureDataForFreeLabourCost
                                                .html
                                        ),
                                    }}
                                />
                            )}
                        </>
                    )}
                </>
            )}

            {!pomChecked && (
                <FMButton
                    htmlButtonType="submit"
                    label={headersLabel?.calculateButtonText}
                    type="primary"
                    className="img-spc-calculate-button"
                    disabled={!calculateButtonEnable}
                    onClick={() => {
                        fireEvents(
                            ['spc-calculate-cta'],
                            undefined,
                            {
                                fieldName: headersLabel?.calculateButtonText,
                            },
                            false
                        );
                    }}
                />
            )}
        </section>
    );
};

export default ChooseYourServicePH;
